<script setup lang="ts">
/**
 * Client Acceptance Form (CDD)
 * — 필드 입력이 주이되, Word 문서 안내 문구/섹션 구조를 함께 표시
 * — Add 버튼: Vue 템플릿 ref unwrap 대응 (배열 직접 mutate)
 * — is_pep Yes → PEP 섹션 자동 추가·바인딩 / 동일인 중복 추가 방지
 */
import { onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import CddVarField from './cdd/CddVarField.vue';
import SgPreSigners from './SgPreSigners.vue';
import { SG_PRE_SIGNER_KEYS } from './cdd/sgPreSignersContext';
import { useCddFill } from './cdd/useCddFill';
import { syncPepFromIsPep } from './cdd/syncPepFromIsPep';
import './allotDoc.css';

const fill = useCddFill();

const directors = ref<number[]>([1]);
const shareholders = ref<number[]>([1]);
const ubos = ref<number[]>([1]);
const peps = ref<number[]>([1]);
const mds = ref<number[]>([1]);
const ceos = ref<number[]>([1]);
const partners = ref<number[]>([1]);
const others = ref<number[]>([1]);
/** CDD → Stage 연동용 (Form 45B / Notices) */
const secretaries = ref<number[]>([1]);
const indControllers = ref<number[]>([1]);
const corpControllers = ref<number[]>([1]);

let seq = { d: 1, s: 1, u: 1, p: 1, m: 1, c: 1, pa: 1, o: 1, sec: 1, ic: 1, cc: 1 };
let syncingPep = false;

/** 템플릿에서 ref가 unwrap 되므로 배열을 직접 변경 */
function addRow(list: number[], key: keyof typeof seq) {
  seq[key] += 1;
  list.push(seq[key]);
}

/** 수동 + Add PEP — 마지막 행이 비어 있으면 추가하지 않음 */
function addPepRow() {
  const last = peps.value[peps.value.length - 1];
  if (last != null && !String(fill.values[`pep${last}_pep_name`] || '').trim()) return;
  addRow(peps.value, 'p');
  fill.setValue(`pep${seq.p}_pep_source`, 'manual');
}

function runPepSync() {
  if (syncingPep) return;
  syncingPep = true;
  try {
    syncPepFromIsPep(
      {
        get: (k) => String(fill.values[k] || ''),
        set: (k, v) => fill.setValue(k, v),
        clearPepId: (id) => clearIndexedId('pep', id),
        setPepIds: (ids) => {
          peps.value = ids.length ? ids : [1];
          seq.p = Math.max(seq.p, ...peps.value);
        },
        nextPepId: () => {
          seq.p += 1;
          return seq.p;
        },
        setHasPep: (v) => {
          if (yn.has_pep !== v) {
            yn.has_pep = v;
            fill.setValue('has_pep', v);
          }
        },
      },
      {
        directors: directors.value,
        shareholders: shareholders.value,
        ubos: ubos.value,
        peps: peps.value,
      },
    );
  } finally {
    syncingPep = false;
  }
}

const INDEX_PREFIX: Record<keyof typeof seq, string> = {
  d: 'dir',
  s: 'sh',
  u: 'ubo',
  p: 'pep',
  m: 'md',
  c: 'ceo',
  pa: 'pa',
  o: 'ot',
  sec: 'sec',
  ic: 'ictrl',
  cc: 'cctrl',
};

function clearIndexedId(prefix: string, id: number) {
  const re = new RegExp(`^${prefix}${id}_`);
  for (const k of Object.keys(fill.values)) {
    if (re.test(k)) fill.setValue(k, '');
  }
}

function removeRow(list: number[], id: number, key: keyof typeof seq) {
  if (list.length <= 1) return;
  const idx = list.indexOf(id);
  if (idx >= 0) list.splice(idx, 1);
  clearIndexedId(INDEX_PREFIX[key], id);
}

type Yn = '' | 'yes' | 'no';
const yn = reactive<Record<string, Yn>>({
  has_corporate_shareholder: '',
  has_pep: '',
  ihc_is_public: '',
  upc_is_public: '',
});
const ynKeys = Object.keys(yn);

function syncYnFromFill() {
  for (const k of ynKeys) {
    const v = String(fill.values[k] || '').toLowerCase();
    yn[k] = v === 'yes' || v === 'true' ? 'yes' : v === 'no' || v === 'false' ? 'no' : '';
  }
}

/** autofill 된 pep2_* / dir2_* 등 키로 반복 행 수를 맞춤 (restoreRows 레이스 대비) */
function maxIndexedFromFill(prefix: string): number {
  let max = 0;
  const re = new RegExp(`^${prefix}(\\d+)_`);
  for (const k of Object.keys(fill.values)) {
    if (prefix === 'pep' && /_pep_(source|id_no)$/.test(k)) continue;
    const m = re.exec(k);
    if (!m) continue;
    if (!String(fill.values[k] || '').trim()) continue;
    max = Math.max(max, Number(m[1]) || 0);
  }
  return max;
}

/** 확장·축소 모두 — entity change 후 빈 UBO 2 행이 남지 않도록 */
function alignRows(r: typeof directors, key: keyof typeof seq, n: number) {
  const target = Math.max(1, n);
  const cur = r.value;
  const already =
    cur.length === target && cur.every((id, i) => id === i + 1);
  if (already) {
    seq[key] = Math.max(seq[key], target);
    return;
  }
  // 줄어드는 id 의 잔여 값 제거 (재-expand / % 계산 오염 방지)
  for (const id of cur) {
    if (id > target) clearIndexedId(INDEX_PREFIX[key], id);
  }
  r.value = Array.from({ length: target }, (_, i) => i + 1);
  seq[key] = Math.max(seq[key], target);
}

function syncRowsFromFill() {
  alignRows(directors, 'd', maxIndexedFromFill('dir'));
  alignRows(shareholders, 's', maxIndexedFromFill('sh'));
  alignRows(ubos, 'u', maxIndexedFromFill('ubo'));
  // PEP 행은 is_pep / 동일인 규칙으로 runPepSync 가 관리
  alignRows(mds, 'm', maxIndexedFromFill('md'));
  alignRows(ceos, 'c', maxIndexedFromFill('ceo'));
  alignRows(partners, 'pa', maxIndexedFromFill('pa'));
  alignRows(others, 'o', maxIndexedFromFill('ot'));
  alignRows(secretaries, 'sec', maxIndexedFromFill('sec'));
  alignRows(indControllers, 'ic', maxIndexedFromFill('ictrl'));
  alignRows(corpControllers, 'cc', maxIndexedFromFill('cctrl'));
  syncYnFromFill();
  runPepSync();
}

onMounted(() => {
  ynKeys.forEach((k) => fill.registerKey(k));
  syncRowsFromFill();
});
onBeforeUnmount(() => ynKeys.forEach((k) => fill.unregisterKey(k)));

watch(
  () => ynKeys.map((k) => fill.values[k]).join('|'),
  () => syncYnFromFill(),
);

/** autofill 이 마운트 이후에 값을 채우면 PEP/주주 행을 다시 맞춤 */
watch(
  () =>
    Object.keys(fill.values)
      .filter((k) => /^(dir|sh|ubo|pep|md|ceo|pa|ot|sec|ictrl|cctrl)\d+_/.test(k))
      .map((k) => `${k}=${fill.values[k]}`)
      .join('|'),
  () => syncRowsFromFill(),
);

function setYn(key: string, val: Yn) {
  yn[key] = yn[key] === val ? '' : val;
  fill.setValue(key, yn[key]);
}

function gatherRows() {
  return {
    directors: [...directors.value],
    shareholders: [...shareholders.value],
    ubos: [...ubos.value],
    peps: [...peps.value],
    mds: [...mds.value],
    ceos: [...ceos.value],
    partners: [...partners.value],
    others: [...others.value],
    secretaries: [...secretaries.value],
    indControllers: [...indControllers.value],
    corpControllers: [...corpControllers.value],
  };
}

function restoreRows(caf: Record<string, number[]>) {
  const apply = (r: typeof directors, ids: number[] | undefined, key: keyof typeof seq) => {
    if (!ids?.length) {
      alignRows(r, key, 1);
      return;
    }
    r.value = [...ids];
    seq[key] = Math.max(...ids, seq[key]);
  };
  apply(directors, caf.directors, 'd');
  apply(shareholders, caf.shareholders, 's');
  apply(ubos, caf.ubos, 'u');
  apply(peps, caf.peps, 'p');
  apply(mds, caf.mds, 'm');
  apply(ceos, caf.ceos, 'c');
  apply(partners, caf.partners, 'pa');
  apply(others, caf.others, 'o');
  apply(secretaries, caf.secretaries, 'sec');
  apply(indControllers, caf.indControllers, 'ic');
  apply(corpControllers, caf.corpControllers, 'cc');
  // restore 가 1행만 와도 fill 값 기준으로 보강·축소
  syncRowsFromFill();
}

defineExpose({ gatherRows, restoreRows });
</script>

<template>
  <div class="allot-doc">
    <div class="doc-head">
      <div class="doc-lbl-title">CLIENT ACCEPTANCE FORM</div>
      <div class="doc-meta">(Customer Due Diligence)</div>
      <div class="doc-meta" style="margin-top:6px">
        CSP: <CddVarField field-key="csp_name" label="CSP Name" />
      </div>
    </div>

    <p class="doc-p note">
      Please complete the particulars below. Supporting documents (Certificate of Incorporation and other
      relevant documents) must be notarised or certified as true copy within the past 90 days.
    </p>

    <!-- A. Entity -->
    <p class="doc-sec-title">Section A1 — Entity Particulars</p>
    <table class="cdd-tbl">
      <tbody>
        <tr><td class="cdd-lcell">Entity / Company Name</td><td><CddVarField field-key="entity_name" label="Entity Name" /></td></tr>
        <tr><td class="cdd-lcell">Former Names (if any)</td><td><CddVarField field-key="entity_former_names" label="Former Names" /></td></tr>
        <tr><td class="cdd-lcell">UEN / Registration No.</td><td><CddVarField field-key="entity_uen" label="UEN" /></td></tr>
        <tr><td class="cdd-lcell">Registered Address</td><td><CddVarField field-key="entity_registered_address" label="Registered Address" /></td></tr>
        <tr><td class="cdd-lcell">Principal Place of Business</td><td><CddVarField field-key="entity_principal_business_address" label="Principal Business Address" /></td></tr>
        <tr><td class="cdd-lcell">Mailing Address</td><td><CddVarField field-key="entity_mailing_address" label="Mailing Address" /></td></tr>
        <tr><td class="cdd-lcell">Date of Incorporation</td><td><CddVarField field-key="entity_incorporation_date" label="Incorporation Date" /></td></tr>
        <tr><td class="cdd-lcell">Nature of Business / Activity</td><td><CddVarField field-key="entity_business_activity" label="Business Activity" /></td></tr>
        <tr><td class="cdd-lcell">Share Capital Amount &amp; Currency</td><td><CddVarField field-key="entity_share_capital" label="Share Capital" /></td></tr>
        <tr><td class="cdd-lcell">Value of Each Share</td><td><CddVarField field-key="entity_share_value" label="Share Value" /></td></tr>
        <tr><td class="cdd-lcell">Proposed Financial Year End</td><td><CddVarField field-key="entity_proposed_fye" label="Proposed FYE" /></td></tr>
        <tr><td class="cdd-lcell">Contact Person</td><td><CddVarField field-key="entity_contact_person" label="Contact Person" /></td></tr>
        <tr><td class="cdd-lcell">Email</td><td><CddVarField field-key="entity_email" label="Email" /></td></tr>
        <tr><td class="cdd-lcell">Mobile Number</td><td><CddVarField field-key="entity_mobile_number" label="Mobile Number" /></td></tr>
        <tr><td class="cdd-lcell">Business Website</td><td><CddVarField field-key="business_website" label="Business Website" /></td></tr>
        <tr><td class="cdd-lcell">Source of Wealth / Funds</td><td><CddVarField field-key="source_of_wealth_info" label="Source of Wealth Info" /></td></tr>
        <tr><td class="cdd-lcell">Supporting Documents Notes</td><td><CddVarField field-key="supporting_documents_notes" label="Supporting Documents Notes" /></td></tr>
      </tbody>
    </table>

    <p class="doc-p note">
      Name(s) of All Connected Parties (Directors, Managing Directors, CEOs, Partners, Others)
    </p>

    <!-- Connected parties summary lines -->
    <p class="doc-sec-title">Connected Parties (Summary)</p>
    <p class="doc-p sub-h">Managing Director(s) (if any): <span class="cnt">{{ mds.length }}</span></p>
    <div v-for="(id, i) in mds" :key="'md-' + id" class="inline-row">
      <span class="idx">{{ i + 1 }}.</span>
      <CddVarField :field-key="`md${id}_md_name`" label="Managing Director Name" />
      <button v-if="mds.length > 1" type="button" class="sg-x" @click="removeRow(mds, id, 'm')">&times;</button>
    </div>
    <button type="button" class="sg-add" @click="addRow(mds, 'm')">+ Add Managing Director</button>

    <p class="doc-p sub-h">Chief Executive Officer(s) (if any): <span class="cnt">{{ ceos.length }}</span></p>
    <div v-for="(id, i) in ceos" :key="'ceo-' + id" class="inline-row">
      <span class="idx">{{ i + 1 }}.</span>
      <CddVarField :field-key="`ceo${id}_ceo_name`" label="CEO Name" />
      <button v-if="ceos.length > 1" type="button" class="sg-x" @click="removeRow(ceos, id, 'c')">&times;</button>
    </div>
    <button type="button" class="sg-add" @click="addRow(ceos, 'c')">+ Add CEO</button>

    <p class="doc-p sub-h">Partner(s) (if any): <span class="cnt">{{ partners.length }}</span></p>
    <div v-for="(id, i) in partners" :key="'pa-' + id" class="inline-row">
      <span class="idx">{{ i + 1 }}.</span>
      <CddVarField :field-key="`pa${id}_p_name`" label="Partner Name" />
      <button v-if="partners.length > 1" type="button" class="sg-x" @click="removeRow(partners, id, 'pa')">&times;</button>
    </div>
    <button type="button" class="sg-add" @click="addRow(partners, 'pa')">+ Add Partner</button>

    <p class="doc-p sub-h">Other(s) (if any): <span class="cnt">{{ others.length }}</span></p>
    <div v-for="(id, i) in others" :key="'ot-' + id" class="inline-row">
      <span class="idx">{{ i + 1 }}.</span>
      <CddVarField :field-key="`ot${id}_o_name`" label="Other Name" />
      <span class="paren">(</span>
      <CddVarField :field-key="`ot${id}_o_role`" label="Role" />
      <span class="paren">)</span>
      <button v-if="others.length > 1" type="button" class="sg-x" @click="removeRow(others, id, 'o')">&times;</button>
    </div>
    <button type="button" class="sg-add" @click="addRow(others, 'o')">+ Add Other</button>

    <!-- A2 Directors detail -->
    <p class="doc-sec-title">Section A2 — Information of Director(s)</p>
    <p class="doc-p note">
      A PEP includes: (i) a person entrusted with prominent public function; (ii) a person who has stepped down
      from such function; (iii) a family member or close associate of a current or former PEP.
    </p>
    <div v-for="(id, i) in directors" :key="'dir-' + id" class="sg-repeat">
      <div class="sg-repeat-h">
        <span>Director {{ i + 1 }} / {{ directors.length }}</span>
        <button v-if="directors.length > 1" type="button" class="sg-x" @click="removeRow(directors, id, 'd')">&times;</button>
      </div>
      <table class="cdd-tbl">
        <tbody>
          <tr><td class="cdd-lcell">Name (as per NRIC/Passport)</td><td><CddVarField :field-key="`dir${id}_director_name`" label="Director Name" /></td></tr>
          <tr><td class="cdd-lcell">NRIC/FIN/Passport No.</td><td><CddVarField :field-key="`dir${id}_director_identification_no`" label="Identification No." /></td></tr>
          <tr><td class="cdd-lcell">Nationality</td><td><CddVarField :field-key="`dir${id}_director_nationality`" label="Nationality" /></td></tr>
          <tr><td class="cdd-lcell">Date of Birth</td><td><CddVarField :field-key="`dir${id}_director_date_of_birth`" label="Date of Birth" /></td></tr>
          <tr><td class="cdd-lcell">Address</td><td><CddVarField :field-key="`dir${id}_director_address`" label="Address" /></td></tr>
          <tr><td class="cdd-lcell">Email</td><td><CddVarField :field-key="`dir${id}_director_email`" label="Email" /></td></tr>
          <tr><td class="cdd-lcell">Mobile No.</td><td><CddVarField :field-key="`dir${id}_director_mobile_no`" label="Mobile No." /></td></tr>
          <tr><td class="cdd-lcell">Tel No.</td><td><CddVarField :field-key="`dir${id}_director_tel_no`" label="Tel No." /></td></tr>
          <tr>
            <td class="cdd-lcell">Is the Director a PEP?</td>
            <td><CddVarField :field-key="`dir${id}_is_pep`" label="Yes / No" /></td>
          </tr>
        </tbody>
      </table>
    </div>
    <button type="button" class="sg-add" @click="addRow(directors, 'd')">+ Add Director</button>

    <!-- Secretary → Form 45B 연동 -->
    <p class="doc-sec-title">Section A2b — Company Secretary (links to Form 45B)</p>
    <p class="doc-p note">Fill via ✨ Contact lookup — Form 45B is created automatically in Consents stage.</p>
    <div v-for="(id, i) in secretaries" :key="'sec-' + id" class="sg-repeat">
      <div class="sg-repeat-h">
        <span>Secretary {{ i + 1 }} / {{ secretaries.length }}</span>
        <button v-if="secretaries.length > 1" type="button" class="sg-x" @click="removeRow(secretaries, id, 'sec')">&times;</button>
      </div>
      <table class="cdd-tbl">
        <tbody>
          <tr><td class="cdd-lcell">Secretary Name</td><td><CddVarField :field-key="`sec${id}_secretary_name`" label="Secretary Name" /></td></tr>
          <tr><td class="cdd-lcell">Identification No.</td><td><CddVarField :field-key="`sec${id}_secretary_identification_no`" label="Identification No." /></td></tr>
          <tr><td class="cdd-lcell">Nationality</td><td><CddVarField :field-key="`sec${id}_secretary_nationality`" label="Nationality" /></td></tr>
          <tr><td class="cdd-lcell">Address</td><td><CddVarField :field-key="`sec${id}_secretary_address`" label="Address" /></td></tr>
        </tbody>
      </table>
    </div>
    <button type="button" class="sg-add" @click="addRow(secretaries, 'sec')">+ Add Secretary</button>

    <!-- A3 Agent -->
    <p class="doc-sec-title">Section A3 — Information of Agent / Contact Person</p>
    <p class="doc-p note">
      For foreigners: passport &amp; proof of residential address (≤ 90 days). For Singapore Citizens/PRs: NRIC (front &amp; back).
    </p>
    <table class="cdd-tbl">
      <tbody>
        <tr><td class="cdd-lcell">Individual Name</td><td><CddVarField field-key="agent_name" label="Agent Name" /></td></tr>
        <tr><td class="cdd-lcell">NRIC/FIN/Passport/Company Reg. No.</td><td><CddVarField field-key="agent_identification_no" label="Identification No." /></td></tr>
        <tr><td class="cdd-lcell">Company Name (if any)</td><td><CddVarField field-key="agent_company_name" label="Agent Company Name" /></td></tr>
        <tr><td class="cdd-lcell">Nationality</td><td><CddVarField field-key="agent_nationality" label="Nationality" /></td></tr>
        <tr><td class="cdd-lcell">Residential Address</td><td><CddVarField field-key="agent_residential_address" label="Residential Address" /></td></tr>
        <tr><td class="cdd-lcell">Contact No.</td><td><CddVarField field-key="agent_contact_no" label="Contact No." /></td></tr>
        <tr><td class="cdd-lcell">Date of Birth</td><td><CddVarField field-key="agent_date_of_birth" label="Date of Birth" /></td></tr>
        <tr><td class="cdd-lcell">Job Title</td><td><CddVarField field-key="agent_job_title" label="Job Title" /></td></tr>
        <tr><td class="cdd-lcell">Email Address</td><td><CddVarField field-key="agent_email" label="Email" /></td></tr>
      </tbody>
    </table>

    <!-- B Shareholders -->
    <p class="doc-sec-title">Section B — Information of Shareholder(s) and Beneficial Owner(s)</p>
    <p class="doc-p note">Please provide a copy of Group Structure Chart or Control Structure.</p>

    <p class="doc-p sub-h">Individual Shareholder(s)</p>
    <div v-for="(id, i) in shareholders" :key="'sh-' + id" class="sg-repeat">
      <div class="sg-repeat-h">
        <span>Shareholder {{ i + 1 }} / {{ shareholders.length }}</span>
        <button v-if="shareholders.length > 1" type="button" class="sg-x" @click="removeRow(shareholders, id, 's')">&times;</button>
      </div>
      <table class="cdd-tbl">
        <tbody>
          <tr><td class="cdd-lcell">Name</td><td><CddVarField :field-key="`sh${id}_shareholder_name`" label="Shareholder Name" /></td></tr>
          <tr><td class="cdd-lcell">Identification No.</td><td><CddVarField :field-key="`sh${id}_shareholder_identification_no`" label="Identification No." /></td></tr>
          <tr><td class="cdd-lcell">Nationality</td><td><CddVarField :field-key="`sh${id}_shareholder_nationality`" label="Nationality" /></td></tr>
          <tr><td class="cdd-lcell">Date of Birth</td><td><CddVarField :field-key="`sh${id}_shareholder_date_of_birth`" label="Date of Birth" /></td></tr>
          <tr><td class="cdd-lcell">Address</td><td><CddVarField :field-key="`sh${id}_shareholder_address`" label="Address" /></td></tr>
          <tr><td class="cdd-lcell">Email</td><td><CddVarField :field-key="`sh${id}_shareholder_email`" label="Email" /></td></tr>
          <tr><td class="cdd-lcell">Mobile No.</td><td><CddVarField :field-key="`sh${id}_shareholder_mobile_no`" label="Mobile No." /></td></tr>
          <tr><td class="cdd-lcell">No. of Shares</td><td><CddVarField :field-key="`sh${id}_shareholder_number_of_shares`" label="No. of Shares" /></td></tr>
          <tr><td class="cdd-lcell">Is PEP?</td><td><CddVarField :field-key="`sh${id}_is_pep`" label="Yes / No" /></td></tr>
        </tbody>
      </table>
    </div>
    <button type="button" class="sg-add" @click="addRow(shareholders, 's')">+ Add Shareholder</button>

    <p class="doc-p sub-h">Ultimate Beneficial Owner(s)</p>
    <div v-for="(id, i) in ubos" :key="'ubo-' + id" class="sg-repeat">
      <div class="sg-repeat-h">
        <span>UBO {{ i + 1 }} / {{ ubos.length }}</span>
        <button v-if="ubos.length > 1" type="button" class="sg-x" @click="removeRow(ubos, id, 'u')">&times;</button>
      </div>
      <table class="cdd-tbl">
        <tbody>
          <tr><td class="cdd-lcell">Name</td><td><CddVarField :field-key="`ubo${id}_ubo_name`" label="UBO Name" /></td></tr>
          <tr><td class="cdd-lcell">Identification No.</td><td><CddVarField :field-key="`ubo${id}_ubo_identification_no`" label="Identification No." /></td></tr>
          <tr><td class="cdd-lcell">Ownership / Control Structure</td><td><CddVarField :field-key="`ubo${id}_ubo_ownership_structure`" label="Ownership Structure" /></td></tr>
          <tr><td class="cdd-lcell">Residential Address</td><td><CddVarField :field-key="`ubo${id}_ubo_residential_address`" label="Residential Address" /></td></tr>
          <tr><td class="cdd-lcell">Nationality</td><td><CddVarField :field-key="`ubo${id}_ubo_nationality`" label="Nationality" /></td></tr>
          <tr><td class="cdd-lcell">Date of Birth</td><td><CddVarField :field-key="`ubo${id}_ubo_date_of_birth`" label="Date of Birth" /></td></tr>
          <tr><td class="cdd-lcell">Contact No.</td><td><CddVarField :field-key="`ubo${id}_ubo_contact_no`" label="Contact No." /></td></tr>
          <tr><td class="cdd-lcell">Email</td><td><CddVarField :field-key="`ubo${id}_ubo_email`" label="Email" /></td></tr>
          <tr><td class="cdd-lcell">Is PEP?</td><td><CddVarField :field-key="`ubo${id}_is_pep`" label="Yes / No" /></td></tr>
        </tbody>
      </table>
    </div>
    <button type="button" class="sg-add" @click="addRow(ubos, 'u')">+ Add UBO</button>

    <p class="doc-p sub-h">Shares Held by Corporation / LP / LLP / Trust</p>
    <table class="cdd-tbl">
      <tbody>
        <tr>
          <td class="cdd-lcell">Has Corporate Shareholder?</td>
          <td>
            <span class="cdd-cbx" @click="setYn('has_corporate_shareholder', 'yes')">
              <span class="box">{{ yn.has_corporate_shareholder === 'yes' ? '✓' : '' }}</span> Yes
            </span>
            <span class="cdd-cbx" @click="setYn('has_corporate_shareholder', 'no')">
              <span class="box">{{ yn.has_corporate_shareholder === 'no' ? '✓' : '' }}</span> No
            </span>
          </td>
        </tr>
        <tr><td colspan="2" class="cdd-sec">Immediate Holding Company</td></tr>
        <tr><td class="cdd-lcell">Entity Name</td><td><CddVarField field-key="ihc_name" label="IHC Name" /></td></tr>
        <tr><td class="cdd-lcell">Industry</td><td><CddVarField field-key="ihc_industry" label="IHC Industry" /></td></tr>
        <tr><td class="cdd-lcell">Country of Registration</td><td><CddVarField field-key="ihc_country" label="IHC Country" /></td></tr>
        <tr>
          <td class="cdd-lcell">Is the Entity a Public Company?</td>
          <td>
            <span class="cdd-cbx" @click="setYn('ihc_is_public', 'yes')">
              <span class="box">{{ yn.ihc_is_public === 'yes' ? '✓' : '' }}</span> Yes
            </span>
            <span class="cdd-cbx" @click="setYn('ihc_is_public', 'no')">
              <span class="box">{{ yn.ihc_is_public === 'no' ? '✓' : '' }}</span> No
            </span>
          </td>
        </tr>
        <tr><td class="cdd-lcell">Incorporation/Registration No.</td><td><CddVarField field-key="ihc_registration_no" label="IHC Registration No." /></td></tr>
        <tr><td class="cdd-lcell">Stock Exchange (if quoted)</td><td><CddVarField field-key="ihc_stock_exchange" label="IHC Stock Exchange" /></td></tr>
        <tr><td class="cdd-lcell">Nature of ownership and control structure</td><td><CddVarField field-key="ihc_ownership_structure" label="IHC Ownership" /></td></tr>
        <tr><td colspan="2" class="cdd-sec">Ultimate Parent Company</td></tr>
        <tr><td class="cdd-lcell">Entity Name</td><td><CddVarField field-key="upc_name" label="UPC Name" /></td></tr>
        <tr><td class="cdd-lcell">Industry</td><td><CddVarField field-key="upc_industry" label="UPC Industry" /></td></tr>
        <tr><td class="cdd-lcell">Country of Registration</td><td><CddVarField field-key="upc_country" label="UPC Country" /></td></tr>
        <tr>
          <td class="cdd-lcell">Is the Entity a Public Company?</td>
          <td>
            <span class="cdd-cbx" @click="setYn('upc_is_public', 'yes')">
              <span class="box">{{ yn.upc_is_public === 'yes' ? '✓' : '' }}</span> Yes
            </span>
            <span class="cdd-cbx" @click="setYn('upc_is_public', 'no')">
              <span class="box">{{ yn.upc_is_public === 'no' ? '✓' : '' }}</span> No
            </span>
          </td>
        </tr>
        <tr><td class="cdd-lcell">Incorporation/Registration No.</td><td><CddVarField field-key="upc_registration_no" label="UPC Registration No." /></td></tr>
        <tr><td class="cdd-lcell">Stock Exchange (if quoted)</td><td><CddVarField field-key="upc_stock_exchange" label="UPC Stock Exchange" /></td></tr>
        <tr><td class="cdd-lcell">Nature of ownership and control structure</td><td><CddVarField field-key="upc_ownership_structure" label="UPC Ownership" /></td></tr>
      </tbody>
    </table>

    <!-- Controllers → Notice docs 연동 -->
    <p class="doc-sec-title">Section B2 — Controllers (links to Controller Notices)</p>
    <p class="doc-p note">Fill via ✨ — Individual / Corporate Controller Notices are created in SH &amp; Controllers stage.</p>

    <p class="doc-p sub-h">Individual Controller(s)</p>
    <div v-for="(id, i) in indControllers" :key="'ictrl-' + id" class="sg-repeat">
      <div class="sg-repeat-h">
        <span>Individual Controller {{ i + 1 }} / {{ indControllers.length }}</span>
        <button v-if="indControllers.length > 1" type="button" class="sg-x" @click="removeRow(indControllers, id, 'ic')">&times;</button>
      </div>
      <table class="cdd-tbl">
        <tbody>
          <tr><td class="cdd-lcell">Full Name</td><td><CddVarField :field-key="`ictrl${id}_controller_full_name`" label="Controller Full Name" /></td></tr>
          <tr><td class="cdd-lcell">Identification No.</td><td><CddVarField :field-key="`ictrl${id}_controller_identification_no`" label="Identification No." /></td></tr>
          <tr><td class="cdd-lcell">Nationality</td><td><CddVarField :field-key="`ictrl${id}_controller_nationality`" label="Nationality" /></td></tr>
          <tr><td class="cdd-lcell">Date of Birth</td><td><CddVarField :field-key="`ictrl${id}_controller_date_of_birth`" label="Date of Birth" /></td></tr>
          <tr><td class="cdd-lcell">Residential Address</td><td><CddVarField :field-key="`ictrl${id}_controller_residential_address`" label="Residential Address" /></td></tr>
          <tr><td class="cdd-lcell">Email</td><td><CddVarField :field-key="`ictrl${id}_controller_email`" label="Email" /></td></tr>
          <tr><td class="cdd-lcell">Contact No.</td><td><CddVarField :field-key="`ictrl${id}_controller_contact_no`" label="Contact No." /></td></tr>
        </tbody>
      </table>
    </div>
    <button type="button" class="sg-add" @click="addRow(indControllers, 'ic')">+ Add Individual Controller</button>

    <p class="doc-p sub-h">Corporate Controller(s)</p>
    <div v-for="(id, i) in corpControllers" :key="'cctrl-' + id" class="sg-repeat">
      <div class="sg-repeat-h">
        <span>Corporate Controller {{ i + 1 }} / {{ corpControllers.length }}</span>
        <button v-if="corpControllers.length > 1" type="button" class="sg-x" @click="removeRow(corpControllers, id, 'cc')">&times;</button>
      </div>
      <table class="cdd-tbl">
        <tbody>
          <tr><td class="cdd-lcell">Corporate Name</td><td><CddVarField :field-key="`cctrl${id}_controller_corporate_name`" label="Corporate Controller Name" /></td></tr>
          <tr><td class="cdd-lcell">UEN / Registration No.</td><td><CddVarField :field-key="`cctrl${id}_controller_uen`" label="UEN" /></td></tr>
          <tr><td class="cdd-lcell">Registered Office</td><td><CddVarField :field-key="`cctrl${id}_controller_registered_office`" label="Registered Office" /></td></tr>
          <tr><td class="cdd-lcell">Email</td><td><CddVarField :field-key="`cctrl${id}_controller_email`" label="Email" /></td></tr>
          <tr><td class="cdd-lcell">Contact No.</td><td><CddVarField :field-key="`cctrl${id}_controller_contact_no`" label="Contact No." /></td></tr>
        </tbody>
      </table>
    </div>
    <button type="button" class="sg-add" @click="addRow(corpControllers, 'cc')">+ Add Corporate Controller</button>

    <p class="doc-p sub-h">Politically Exposed Person(s) — details (if any)</p>
    <table class="cdd-tbl" style="margin-bottom:8px">
      <tbody>
        <tr>
          <td class="cdd-lcell">Has PEP?</td>
          <td>
            <span class="cdd-cbx" @click="setYn('has_pep', 'yes')">
              <span class="box">{{ yn.has_pep === 'yes' ? '✓' : '' }}</span> Yes
            </span>
            <span class="cdd-cbx" @click="setYn('has_pep', 'no')">
              <span class="box">{{ yn.has_pep === 'no' ? '✓' : '' }}</span> No
            </span>
          </td>
        </tr>
      </tbody>
    </table>
    <div v-for="(id, i) in peps" :key="'pep-' + id" class="sg-repeat">
      <div class="sg-repeat-h">
        <span>PEP {{ i + 1 }} / {{ peps.length }}</span>
        <button v-if="peps.length > 1" type="button" class="sg-x" @click="removeRow(peps, id, 'p')">&times;</button>
      </div>
      <table class="cdd-tbl">
        <tbody>
          <tr><td class="cdd-lcell">Name</td><td><CddVarField :field-key="`pep${id}_pep_name`" label="PEP Name" /></td></tr>
          <tr><td class="cdd-lcell">Country</td><td><CddVarField :field-key="`pep${id}_pep_country`" label="PEP Country" /></td></tr>
          <tr><td class="cdd-lcell">Function / Position</td><td><CddVarField :field-key="`pep${id}_pep_function`" label="PEP Function" /></td></tr>
          <tr><td class="cdd-lcell">Period</td><td><CddVarField :field-key="`pep${id}_pep_period`" label="PEP Period" /></td></tr>
          <tr><td class="cdd-lcell">Relationship</td><td><CddVarField :field-key="`pep${id}_pep_relationship`" label="PEP Relationship" /></td></tr>
          <tr><td class="cdd-lcell">Category</td><td><CddVarField :field-key="`pep${id}_pep_category`" label="PEP Category" /></td></tr>
        </tbody>
      </table>
    </div>
    <button type="button" class="sg-add" @click="addPepRow">+ Add PEP</button>

    <!-- Declaration -->
    <p class="doc-sec-title">Declaration</p>
    <p class="doc-p note">
      I confirm that the facts and information stated in this form are true and correct to the best of my knowledge.
    </p>
    <table class="cdd-tbl">
      <tbody>
        <tr><td class="cdd-lcell">Declarant Name</td><td><CddVarField field-key="declarant_name" label="Declarant Name" /></td></tr>
        <tr><td class="cdd-lcell">Declarant Identification No.</td><td><CddVarField field-key="declarant_identification_no" label="Declarant ID No." /></td></tr>
        <tr><td class="cdd-lcell">Declaration Date</td><td><CddVarField field-key="declaration_date" label="Declaration Date" /></td></tr>
      </tbody>
    </table>

    <p class="doc-sec-title">Signatories</p>
    <SgPreSigners :var-key="SG_PRE_SIGNER_KEYS.client" />
  </div>
</template>

<style scoped>
.note { font-size: 11.5px; color: #555; line-height: 1.5; margin: 6px 0 12px; }
.sub-h { font-weight: 700; margin: 14px 0 6px; font-size: 12.5px; }
.cnt { color: #1B84FF; font-weight: 800; }
.inline-row {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  margin-bottom: 6px; padding: 6px 8px;
  border: 1px dashed #c9d2e3; border-radius: 8px;
}
.idx { font-weight: 700; color: #6b7a94; min-width: 18px; }
.paren { color: #6b7a94; }
.sg-repeat { margin: 8px 0 10px; border: 1px dashed #c9d2e3; border-radius: 8px; padding: 8px 10px; }
.sg-repeat-h { display: flex; align-items: center; justify-content: space-between; font-size: 12px; font-weight: 700; color: #42506a; margin-bottom: 6px; }
.sg-x { border: none; background: transparent; color: #c0392b; font-size: 18px; cursor: pointer; line-height: 1; padding: 0 4px; }
.sg-add {
  display: inline-flex; margin: 4px 0 16px;
  border: 1px solid #1B84FF; background: #eaf3ff; color: #1B84FF;
  border-radius: 8px; padding: 6px 12px; font-size: 12px; font-weight: 700; cursor: pointer;
}
.sg-add:hover { background: #dcebff; }
:deep(.cdd-cbx) {
  display: inline-flex; align-items: center; gap: 4px; margin-right: 14px;
  white-space: nowrap; cursor: pointer; user-select: none;
}
:deep(.cdd-cbx .box) {
  display: inline-flex; width: 13px; height: 13px; border: 1.5px solid #111;
  align-items: center; justify-content: center; font-size: 10px; font-weight: 800; background: #fff;
}
</style>
